import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  RouterStateSnapshot,
} from '@angular/router';
import { forkJoin, Observable, of } from 'rxjs';
import { AccountDataStore } from '../../account-data/account-data.store';
import { AuthDataDispatcher } from '../../auth-data/auth-data.dispatcher';
import { ErrorService } from '../../error-reporting/error.service';
import { FeaturesDataStore } from '../../features-data/features-data.store';
import { GeolocationDataService } from '../../geolocation-data/geolocation-data.service';
import { LocalizationService } from '../../localization/localization.service';
import { OnboardingDataStore } from '../../onboarding-data/onboarding-data.store';
import { SettingsDataStore } from '../../settings-data/settings-data.store';
import { catchError, map } from 'rxjs/operators';

@Injectable()
export class AppDataGuard implements CanActivate, CanActivateChild {
  constructor(
    private _localizationService: LocalizationService,
    private _settingsDataStore: SettingsDataStore,
    private _errorService: ErrorService,
    private _authDataDispatcher: AuthDataDispatcher,
    private _geolocationDataService: GeolocationDataService,
    private _accountDataStore: AccountDataStore,
    private _featuresDataStore: FeaturesDataStore,
    private _onboardingDataStore: OnboardingDataStore
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.loadAllNecessaryData(state.url);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.canActivate(route, state);
  }

  loadAllNecessaryData(currentUrl: string): Observable<boolean> {
    return forkJoin([
      this._settingsDataStore.getSettings(),
      this._accountDataStore.getAccount(),
      this._accountDataStore.getUser(),
      this._localizationService.isTranslationsReady(),
      this._featuresDataStore.getFeaturesData(),
      this._onboardingDataStore.getAccountMigration(),
      this._geolocationDataService.getLocationInfo(), // get location information to use throughout app (e.g. map card)
    ]).pipe(
      map(
        ([
          settings,
          account,
          user,
          isTranslationsReady,
          featuresData,
          accountMigration,
          _,
        ]) => {
          const missingData: boolean =
            settings === null ||
            account === null ||
            user === null ||
            !isTranslationsReady ||
            featuresData === null ||
            accountMigration === null;
          if (missingData) {
            this._authDataDispatcher.logout(true, currentUrl);
          }

          return !missingData;
        }
      ),
      catchError((error) => {
        this._errorService.alertSystem(
          new Error(`app data guard error: ${error}`)
        );

        this._authDataDispatcher.logout(true, currentUrl);

        return of(false);
      })
    );
  }
}
